import {type BskyAgent} from '@atproto/api'
import {nanoid} from 'nanoid/non-secure'

import {
  acceptInvite,
  getClient,
  inviteToRoom,
  leaveRoom,
  loginMatrix,
  loginWithToken,
  normalizeMatrixBaseUrl,
  setMatrixBaseUrl,
  waitForInitialSync,
} from '#/lib/matrixClient'
import {normalizeBskyHandle} from '#/lib/matrixId'
import {CHAT_DEFAULT_HOMESERVER_URL} from '#/lib/constants'
import {loadMatrixConfig, loadMatrixSession, saveMatrixSession} from '#/lib/storage/matrix'
import {validateMatrixId} from '#/lib/tellusProfileRecord'
import {logger} from '#/logger'
import {device} from '#/storage'
import {usePrivateStore} from './privateStore'
import {
  PRIVATE_COMMENT_EVENT,
  PRIVATE_ENDPOINTS_COLLECTION,
  PRIVATE_ENDPOINTS_RKEY,
  PRIVATE_FOLLOW_ACTION_EVENT,
  PRIVATE_FOLLOW_REQUEST_EVENT,
  PRIVATE_HIDDEN_ROOMS_ACCOUNT_DATA,
  PRIVATE_MATRIX_PROFILE_COLLECTION,
  PRIVATE_MATRIX_PROFILE_RKEY,
  PRIVATE_POST_EVENT,
  PRIVATE_PRIVACY_SETTINGS_COLLECTION,
  PRIVATE_PRIVACY_SETTINGS_RKEY,
  PRIVATE_REACTION_EVENT,
  PRIVATE_ROOMS_ACCOUNT_DATA,
  PRIVATE_STORY_EVENT,
  PRIVATE_SYSTEM_EVENT,
  type PrivateEndpointsRecord,
  type PrivateFollowRequest,
  type PrivateFollowRequestActionEvent,
  type PrivateFollowRequestEvent,
  type PrivateMatrixProfileRecord,
  type PrivatePostEvent,
  type PrivatePrivacySettingsRecord,
  type PrivateProfileState,
  type PrivateRoomsAccountData,
  type PrivateStoryEvent,
  type PrivateSystemKind,
  type PrivateRoomKindConfig,
} from './privateTypes'

type MatrixSessionState = {
  accessToken: string
  userId: string
  deviceId?: string
}

type MatrixCredentials = {
  mxid: string
  password: string
  createdAt: string
}

const MATRIX_CREDS_KEY = 'privateMatrixCredentials'
const CACHE_TTL_MS = 5 * 60 * 1000

const privacyCache = new Map<
  string,
  {value: PrivatePrivacySettingsRecord | null; ts: number}
>()
const endpointsCache = new Map<
  string,
  {value: PrivateEndpointsRecord | null; ts: number}
>()
const matrixProfileCache = new Map<
  string,
  {value: PrivateMatrixProfileRecord | null; ts: number}
>()

function nowIso() {
  return new Date().toISOString()
}

function isFresh(ts: number) {
  return Date.now() - ts < CACHE_TTL_MS
}

function getAgentDid(agent: BskyAgent) {
  return agent?.session?.did || agent?.assertDid
}

function getAgentHandle(agent: BskyAgent) {
  return agent?.session?.handle || ''
}

function loadMatrixCredentials(did: string): MatrixCredentials | null {
  const stored = device.get([MATRIX_CREDS_KEY as never]) as
    | Record<string, MatrixCredentials>
    | undefined
  if (!stored || typeof stored !== 'object') return null
  const creds = stored[did]
  if (!creds?.mxid || !creds?.password) return null
  return creds
}

function saveMatrixCredentials(did: string, creds: MatrixCredentials) {
  const stored = (device.get([MATRIX_CREDS_KEY as never]) ??
    {}) as Record<string, MatrixCredentials>
  stored[did] = creds
  device.set([MATRIX_CREDS_KEY as never], stored as never)
}

function sanitizeMatrixLocalpart(input: string) {
  const cleaned = input.toLowerCase().replace(/[^a-z0-9._=\/-]/g, '_')
  return cleaned.slice(0, 64) || `tellus_${nanoid(6)}`
}

async function resolveMatrixBaseUrl() {
  const config = await loadMatrixConfig()
  const baseUrl = normalizeMatrixBaseUrl(config?.homeserver || '')
  return baseUrl || CHAT_DEFAULT_HOMESERVER_URL
}

async function registerMatrixUser({
  baseUrl,
  handle,
}: {
  baseUrl: string
  handle: string
}): Promise<MatrixSessionState & {password: string}> {
  const client = getClient()
  const localpartBase = sanitizeMatrixLocalpart(handle || `tellus_${nanoid(6)}`)
  const password = nanoid(32)
  const tryRegister = async (username: string, sessionId?: string) => {
    return client.register(
      username,
      password,
      sessionId,
      {type: 'm.login.dummy'},
      undefined,
      undefined,
      false,
    )
  }

  let lastError: any = null
  for (let i = 0; i < 3; i += 1) {
    const username = i === 0 ? localpartBase : `${localpartBase}_${nanoid(4)}`
    try {
      const response = await tryRegister(username)
      const accessToken = response?.access_token
      const userId = response?.user_id
      const deviceId = response?.device_id
      if (accessToken && userId) {
        return {accessToken, userId, deviceId, password}
      }
      lastError = new Error('Matrix registration response invalid')
    } catch (err: any) {
      const errcode = err?.errcode || err?.data?.errcode
      const sessionId = err?.session || err?.data?.session
      const flows = err?.flows || err?.data?.flows
      if (errcode === 'M_USER_IN_USE') {
        lastError = err
        continue
      }
      if (sessionId && Array.isArray(flows)) {
        const hasDummy = flows.some(flow =>
          (flow?.stages || []).includes('m.login.dummy'),
        )
        if (hasDummy) {
          try {
            const response = await tryRegister(username, sessionId)
            const accessToken = response?.access_token
            const userId = response?.user_id
            const deviceId = response?.device_id
            if (accessToken && userId) {
              return {accessToken, userId, deviceId, password}
            }
            lastError = new Error('Matrix registration response invalid')
          } catch (authErr) {
            lastError = authErr
          }
          continue
        }
      }
      lastError = err
      break
    }
  }

  logger.error('Matrix registration failed', {baseUrl, err: lastError})
  throw lastError || new Error('Matrix registration failed')
}

async function acceptPendingPrivateInvites() {
  const client = getClient()
  if (!client) return
  const rooms = client.getRooms?.() || []
  for (const room of rooms) {
    if (!room || room.getMyMembership?.() !== 'invite') continue
    const stateEvent = room.currentState?.getStateEvents?.(
      PRIVATE_SYSTEM_EVENT,
      '',
    )
    const content = stateEvent?.getContent?.()
    const kind = content?.kind
    if (!kind) continue
    try {
      await acceptInvite(room.roomId)
      await hideRoomsInAccountData([room.roomId], kind, content?.ownerDid || '')
    } catch (err) {
      logger.warn('Failed to auto-accept private invite', {
        roomId: room.roomId,
        err,
      })
    }
  }
}

function syncApprovedPrivateRooms() {
  const client = getClient()
  if (!client) return
  const rooms = client.getRooms?.() || []
  for (const room of rooms) {
    if (!room || room.getMyMembership?.() !== 'join') continue
    const stateEvent = room.currentState?.getStateEvents?.(
      PRIVATE_SYSTEM_EVENT,
      '',
    )
    const content = stateEvent?.getContent?.()
    const ownerDid = content?.ownerDid
    const kind = content?.kind
    if (!ownerDid || (kind !== 'privateFeed' && kind !== 'privateAudience')) {
      continue
    }
    usePrivateStore.getState().setProfileState(ownerDid, {
      isPrivate: true,
      approved: true,
      requestState: 'approved',
    })
  }
}

async function sendSystemEvent(roomId: string, kind: PrivateSystemKind, ownerDid: string) {
  const client = getClient()
  if (!client) return
  try {
    await client.sendStateEvent(
      roomId,
      PRIVATE_SYSTEM_EVENT,
      {kind, ownerDid},
      '',
    )
  } catch (err) {
    logger.warn('Failed to mark private system room', {roomId, err})
  }
}

async function hideRoomsInAccountData(roomIds: string[], kind: PrivateSystemKind, ownerDid: string) {
  const client = getClient()
  if (!client) return
  try {
    const existing =
      client.getAccountData?.(PRIVATE_HIDDEN_ROOMS_ACCOUNT_DATA)?.getContent?.() ||
      {}
    const roomMap: Record<string, {kind: PrivateSystemKind; ownerDid?: string}> =
      {...(existing.roomIds || {})}
    for (const id of roomIds) {
      roomMap[id] = {kind, ownerDid}
    }
    await client.setAccountData(PRIVATE_HIDDEN_ROOMS_ACCOUNT_DATA, {
      roomIds: roomMap,
    })
  } catch (err) {
    logger.warn('Failed to hide Matrix rooms', {roomIds, err})
  }
}

async function createPrivateRoom(
  kindConfig: PrivateRoomKindConfig,
  ownerDid: string,
) {
  const client = getClient()
  if (!client) {
    throw new Error('Matrix client not initialized')
  }

  const initialState = [
    {
      type: 'm.room.encryption',
      state_key: '',
      content: {algorithm: 'm.megolm.v1.aes-sha2'},
    },
    {
      type: 'm.room.join_rules',
      state_key: '',
      content: {join_rule: kindConfig.joinRule},
    },
  ] as any[]

  if (kindConfig.historyVisibility) {
    initialState.push({
      type: 'm.room.history_visibility',
      state_key: '',
      content: {history_visibility: kindConfig.historyVisibility},
    })
  }

  const res = await client.createRoom({
    name: `tellus-${kindConfig.kind}`,
    preset: 'private_chat',
    is_direct: false,
    initial_state: initialState,
  })
  const roomId = res?.room_id
  if (!roomId) throw new Error('Matrix room creation failed')
  await sendSystemEvent(roomId, kindConfig.kind, ownerDid)
  await hideRoomsInAccountData([roomId], kindConfig.kind, ownerDid)
  return roomId
}

async function getRoomMembership(roomId: string, userId: string) {
  const client = getClient()
  const room = client?.getRoom?.(roomId)
  if (!room) return null
  if (typeof room.getMyMembership === 'function') {
    return room.getMyMembership()
  }
  const member = room.getMember?.(userId)
  return member?.membership ?? null
}

async function listRoomEvents(roomId: string, limit = 200) {
  const client = getClient()
  const room = client?.getRoom?.(roomId)
  if (!room) return []
  try {
    if (typeof client.scrollback === 'function') {
      await client.scrollback(room, limit)
    }
  } catch {}
  const timeline = room.getLiveTimeline?.()
  return timeline?.getEvents?.() ?? room.timeline ?? []
}

function parseMatrixEvent(event: any) {
  const type = event?.getType?.() ?? event?.event?.type ?? event?.type
  const content = event?.getContent?.() ?? event?.event?.content ?? event?.content
  const sender = event?.getSender?.() ?? event?.event?.sender ?? ''
  const ts = event?.getTs?.() ?? event?.event?.origin_server_ts ?? Date.now()
  return {type, content, sender, ts}
}

export async function ensureSilentMatrixIdentity({
  agent,
  ownerDid,
  ownerHandle,
}: {
  agent: BskyAgent
  ownerDid?: string
  ownerHandle?: string
}) {
  const did = ownerDid || getAgentDid(agent)
  if (!did) {
    throw new Error('Missing DID for Matrix provisioning')
  }
  const handle = ownerHandle || getAgentHandle(agent)
  const baseUrl = await resolveMatrixBaseUrl()
  setMatrixBaseUrl(baseUrl)

  const existing = await loadMatrixSession()
  if (existing?.accessToken && existing?.userId) {
    loginWithToken({
      access_token: existing.accessToken,
      user_id: existing.userId,
      device_id: existing.deviceId,
    })
    try {
      await waitForInitialSync({timeoutMs: 15000})
      await acceptPendingPrivateInvites()
      syncApprovedPrivateRooms()
    } catch {}
    publishMatrixProfile(agent, existing.userId).catch(() => {})
    return {mxid: existing.userId, client: getClient()}
  }

  const creds = loadMatrixCredentials(did)
  if (creds?.mxid && creds?.password) {
    const res = await loginMatrix(creds.mxid, creds.password, {timeoutMs: 15000})
    const mxid = res?.user_id || creds.mxid
    try {
      await waitForInitialSync({timeoutMs: 15000})
      await acceptPendingPrivateInvites()
      syncApprovedPrivateRooms()
    } catch {}
    publishMatrixProfile(agent, mxid).catch(() => {})
    return {mxid, client: getClient()}
  }

  const registered = await registerMatrixUser({
    baseUrl,
    handle: normalizeBskyHandle(handle || nanoid(6)),
  })
  const session: MatrixSessionState = {
    accessToken: registered.accessToken,
    userId: registered.userId,
    deviceId: registered.deviceId,
  }
  await saveMatrixSession(session)
  loginWithToken({
    access_token: registered.accessToken,
    user_id: registered.userId,
    device_id: registered.deviceId,
  })
  saveMatrixCredentials(did, {
    mxid: registered.userId,
    password: registered.password,
    createdAt: nowIso(),
  })
  try {
    await waitForInitialSync({timeoutMs: 15000})
    await acceptPendingPrivateInvites()
    syncApprovedPrivateRooms()
  } catch {}
  publishMatrixProfile(agent, registered.userId).catch(() => {})
  return {mxid: registered.userId, client: getClient()}
}

export async function ensurePrivateRooms(agent: BskyAgent, ownerDid?: string) {
  const did = ownerDid || getAgentDid(agent)
  if (!did) {
    throw new Error('Missing DID for private rooms')
  }
  const {mxid} = await ensureSilentMatrixIdentity({agent, ownerDid: did})
  const client = getClient()
  if (!client) {
    throw new Error('Matrix client not initialized')
  }

  const existing = client.getAccountData?.(PRIVATE_ROOMS_ACCOUNT_DATA)
  const content = existing?.getContent?.() as PrivateRoomsAccountData | undefined
  if (
    content?.audienceRoomId &&
    content?.feedRoomId &&
    content?.requestsRoomId
  ) {
    return content
  }

  const configs: Record<PrivateSystemKind, PrivateRoomKindConfig> = {
    privateAudience: {kind: 'privateAudience', joinRule: 'invite'},
    privateFeed: {kind: 'privateFeed', joinRule: 'invite'},
    privateRequests: {
      kind: 'privateRequests',
      // Requests room is joinable so requesters can post events without a visible DM.
      joinRule: 'public',
      historyVisibility: 'joined',
    },
  }

  const audienceRoomId = await createPrivateRoom(configs.privateAudience, did)
  const feedRoomId = await createPrivateRoom(configs.privateFeed, did)
  const requestsRoomId = await createPrivateRoom(configs.privateRequests, did)

  const accountData: PrivateRoomsAccountData = {
    audienceRoomId,
    feedRoomId,
    requestsRoomId,
    ownerDid: did,
    updatedAt: nowIso(),
  }
  try {
    await client.setAccountData(PRIVATE_ROOMS_ACCOUNT_DATA, accountData)
  } catch (err) {
    logger.warn('Failed to persist private rooms account data', {err})
  }

  await publishMatrixProfile(agent, mxid)
  await publishPrivateEndpoints(agent, {
    requestsRoomId,
    audienceRoomId,
    feedRoomId,
  })

  return accountData
}

export async function publishMatrixProfile(agent: BskyAgent, mxid: string) {
  const did = getAgentDid(agent)
  if (!did) return
  await agent.api.com.atproto.repo.putRecord({
    repo: did,
    collection: PRIVATE_MATRIX_PROFILE_COLLECTION,
    rkey: PRIVATE_MATRIX_PROFILE_RKEY,
    record: {
      $type: PRIVATE_MATRIX_PROFILE_COLLECTION,
      mxid,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    },
  })
  matrixProfileCache.set(did, {
    value: {
      $type: PRIVATE_MATRIX_PROFILE_COLLECTION,
      mxid,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    },
    ts: Date.now(),
  })
}

export async function publishPrivacySettings(
  agent: BskyAgent,
  isPrivate: boolean,
) {
  const did = getAgentDid(agent)
  if (!did) return
  const record: PrivatePrivacySettingsRecord = {
    $type: PRIVATE_PRIVACY_SETTINGS_COLLECTION,
    isPrivate,
    updatedAt: nowIso(),
  }
  await agent.api.com.atproto.repo.putRecord({
    repo: did,
    collection: PRIVATE_PRIVACY_SETTINGS_COLLECTION,
    rkey: PRIVATE_PRIVACY_SETTINGS_RKEY,
    record,
  })
  privacyCache.set(did, {value: record, ts: Date.now()})
  usePrivateStore.getState().setProfileState(did, {
    isPrivate,
    approved: true,
    requestState: 'approved',
  })
}

export async function publishPrivateEndpoints(
  agent: BskyAgent,
  endpoints: {
    requestsRoomId: string
    audienceRoomId?: string
    feedRoomId?: string
  },
) {
  const did = getAgentDid(agent)
  if (!did) return
  const record: PrivateEndpointsRecord = {
    $type: PRIVATE_ENDPOINTS_COLLECTION,
    requestsRoomId: endpoints.requestsRoomId,
    audienceRoomId: endpoints.audienceRoomId,
    feedRoomId: endpoints.feedRoomId,
    updatedAt: nowIso(),
  }
  await agent.api.com.atproto.repo.putRecord({
    repo: did,
    collection: PRIVATE_ENDPOINTS_COLLECTION,
    rkey: PRIVATE_ENDPOINTS_RKEY,
    record,
  })
  endpointsCache.set(did, {value: record, ts: Date.now()})
}

export async function getPrivacySettings(
  targetDid: string,
  agent: BskyAgent,
) {
  const cached = privacyCache.get(targetDid)
  if (cached && isFresh(cached.ts)) return cached.value
  try {
    const {data} = await agent.api.com.atproto.repo.getRecord({
      repo: targetDid,
      collection: PRIVATE_PRIVACY_SETTINGS_COLLECTION,
      rkey: PRIVATE_PRIVACY_SETTINGS_RKEY,
    })
    const record = data?.value as PrivatePrivacySettingsRecord | undefined
    const normalized = record?.isPrivate
      ? record
      : record
        ? {...record, isPrivate: false}
        : null
    privacyCache.set(targetDid, {value: normalized, ts: Date.now()})
    return normalized
  } catch (err: any) {
    if (err?.message?.includes('Could not locate record')) {
      privacyCache.set(targetDid, {value: null, ts: Date.now()})
      return null
    }
    throw err
  }
}

export async function getPrivateEndpoints(
  targetDid: string,
  agent: BskyAgent,
) {
  const cached = endpointsCache.get(targetDid)
  if (cached && isFresh(cached.ts)) return cached.value
  try {
    const {data} = await agent.api.com.atproto.repo.getRecord({
      repo: targetDid,
      collection: PRIVATE_ENDPOINTS_COLLECTION,
      rkey: PRIVATE_ENDPOINTS_RKEY,
    })
    const record = data?.value as PrivateEndpointsRecord | undefined
    const normalized = record?.requestsRoomId ? record : null
    endpointsCache.set(targetDid, {value: normalized, ts: Date.now()})
    return normalized
  } catch (err: any) {
    if (err?.message?.includes('Could not locate record')) {
      endpointsCache.set(targetDid, {value: null, ts: Date.now()})
      return null
    }
    throw err
  }
}

export async function getMatrixProfile(
  targetDid: string,
  agent: BskyAgent,
) {
  const cached = matrixProfileCache.get(targetDid)
  if (cached && isFresh(cached.ts)) return cached.value
  try {
    const {data} = await agent.api.com.atproto.repo.getRecord({
      repo: targetDid,
      collection: PRIVATE_MATRIX_PROFILE_COLLECTION,
      rkey: PRIVATE_MATRIX_PROFILE_RKEY,
    })
    const record = data?.value as PrivateMatrixProfileRecord | undefined
    const normalized = record?.mxid ? record : null
    matrixProfileCache.set(targetDid, {value: normalized, ts: Date.now()})
    return normalized
  } catch (err: any) {
    if (err?.message?.includes('Could not locate record')) {
      matrixProfileCache.set(targetDid, {value: null, ts: Date.now()})
      return null
    }
    throw err
  }
}

export async function getPrivateState({
  targetDid,
  agent,
  checkMembership = false,
}: {
  targetDid: string
  agent: BskyAgent
  checkMembership?: boolean
}): Promise<PrivateProfileState> {
  const store = usePrivateStore.getState()
  const cached = store.profiles[targetDid]
  const privacy = await getPrivacySettings(targetDid, agent)
  if (!privacy?.isPrivate) {
    const state: PrivateProfileState = {
      isPrivate: false,
      approved: true,
      requestState: 'none',
      updatedAt: Date.now(),
    }
    store.setProfileState(targetDid, state)
    return state
  }

  let approved = cached?.approved ?? false
  let requestState = cached?.requestState ?? 'none'
  let requestId = cached?.requestId

  if (checkMembership) {
    try {
      const session = await ensureSilentMatrixIdentity({agent})
      const endpoints = await getPrivateEndpoints(targetDid, agent)
      const roomId = endpoints?.feedRoomId || endpoints?.audienceRoomId
      if (roomId && session?.mxid) {
        const membership = await getRoomMembership(roomId, session.mxid)
        if (membership === 'join' || membership === 'invite') {
          approved = true
          requestState = 'approved'
        }
      }
    } catch (err) {
      logger.warn('Private state membership check failed', {targetDid, err})
    }
  }

  const state: PrivateProfileState = {
    isPrivate: true,
    approved,
    requestState,
    requestId,
    updatedAt: Date.now(),
  }
  store.setProfileState(targetDid, state)
  return state
}

export async function requestFollow(targetDid: string, agent: BskyAgent) {
  const did = getAgentDid(agent)
  if (!did) throw new Error('Missing DID for follow request')
  const {mxid} = await ensureSilentMatrixIdentity({agent})
  const endpoints = await getPrivateEndpoints(targetDid, agent)
  if (!endpoints?.requestsRoomId) {
    throw new Error('Private requests endpoint unavailable')
  }
  const validation = validateMatrixId(mxid)
  if (!validation.ok) {
    throw new Error('Matrix identity invalid')
  }
  const requestId = nanoid()
  const content: PrivateFollowRequestEvent = {
    id: requestId,
    fromDid: did,
    fromMxid: validation.normalized,
    createdAt: nowIso(),
  }
  const client = getClient()
  if (!client) throw new Error('Matrix client not initialized')
  try {
    await client.joinRoom(endpoints.requestsRoomId)
  } catch {}
  await hideRoomsInAccountData(
    [endpoints.requestsRoomId],
    'privateRequests',
    targetDid,
  )
  await client.sendEvent(
    endpoints.requestsRoomId,
    PRIVATE_FOLLOW_REQUEST_EVENT,
    content,
  )
  usePrivateStore.getState().setRequestState(targetDid, 'requested', requestId)
  return requestId
}

export async function cancelFollowRequest(
  targetDid: string,
  agent: BskyAgent,
) {
  const did = getAgentDid(agent)
  if (!did) throw new Error('Missing DID for cancel request')
  const {profiles} = usePrivateStore.getState()
  const requestId = profiles[targetDid]?.requestId
  if (!requestId) return
  const endpoints = await getPrivateEndpoints(targetDid, agent)
  if (!endpoints?.requestsRoomId) return
  const client = getClient()
  if (!client) return
  try {
    await client.joinRoom(endpoints.requestsRoomId)
  } catch {}
  const content: PrivateFollowRequestActionEvent = {
    requestId,
    action: 'cancel',
    byDid: did,
    createdAt: nowIso(),
  }
  await client.sendEvent(
    endpoints.requestsRoomId,
    PRIVATE_FOLLOW_ACTION_EVENT,
    content,
  )
  usePrivateStore.getState().setRequestState(targetDid, 'none')
}

export async function leavePrivateRooms(
  targetDid: string,
  agent: BskyAgent,
) {
  const endpoints = await getPrivateEndpoints(targetDid, agent)
  if (!endpoints) return
  const client = getClient()
  if (!client) return
  if (endpoints.feedRoomId) {
    await leaveRoom(endpoints.feedRoomId)
  }
  if (endpoints.audienceRoomId) {
    await leaveRoom(endpoints.audienceRoomId)
  }
  usePrivateStore.getState().setProfileState(targetDid, {
    approved: false,
    requestState: 'none',
  })
}

export async function listRequests(agent: BskyAgent, ownerDid?: string) {
  const did = ownerDid || getAgentDid(agent)
  if (!did) throw new Error('Missing DID for request list')
  const rooms = await ensurePrivateRooms(agent, did)
  const events = await listRoomEvents(rooms.requestsRoomId, 500)
  const requests = new Map<string, PrivateFollowRequest>()
  const actions = new Map<string, PrivateFollowRequestActionEvent>()

  for (const raw of events) {
    const {type, content} = parseMatrixEvent(raw)
    if (type === PRIVATE_FOLLOW_REQUEST_EVENT) {
      const parsed = content as PrivateFollowRequestEvent
      if (parsed?.id && parsed?.fromDid && parsed?.fromMxid) {
        requests.set(parsed.id, {
          id: parsed.id,
          fromDid: parsed.fromDid,
          fromMxid: parsed.fromMxid,
          createdAt: parsed.createdAt,
          message: parsed.message,
        })
      }
    }
    if (type === PRIVATE_FOLLOW_ACTION_EVENT) {
      const action = content as PrivateFollowRequestActionEvent
      if (action?.requestId) {
        actions.set(action.requestId, action)
      }
    }
  }

  const pending = Array.from(requests.values())
    .filter(req => {
      const action = actions.get(req.id)
      return !action
    })
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))

  usePrivateStore.getState().setRequests(did, pending)
  return pending
}

export async function acceptRequest(
  agent: BskyAgent,
  request: PrivateFollowRequest,
) {
  const did = getAgentDid(agent)
  if (!did) throw new Error('Missing DID for accept request')
  const rooms = await ensurePrivateRooms(agent, did)
  await inviteToRoom(rooms.audienceRoomId, request.fromMxid)
  await inviteToRoom(rooms.feedRoomId, request.fromMxid)
  const client = getClient()
  if (client) {
    const content: PrivateFollowRequestActionEvent = {
      requestId: request.id,
      action: 'accept',
      byDid: did,
      createdAt: nowIso(),
    }
    await client.sendEvent(
      rooms.requestsRoomId,
      PRIVATE_FOLLOW_ACTION_EVENT,
      content,
    )
  }
}

export async function declineRequest(
  agent: BskyAgent,
  request: PrivateFollowRequest,
) {
  const did = getAgentDid(agent)
  if (!did) throw new Error('Missing DID for decline request')
  const rooms = await ensurePrivateRooms(agent, did)
  const client = getClient()
  if (!client) return
  const content: PrivateFollowRequestActionEvent = {
    requestId: request.id,
    action: 'decline',
    byDid: did,
    createdAt: nowIso(),
  }
  await client.sendEvent(
    rooms.requestsRoomId,
    PRIVATE_FOLLOW_ACTION_EVENT,
    content,
  )
}

export async function postPrivate(
  agent: BskyAgent,
  payload: Omit<PrivatePostEvent, 'id' | 'createdAt' | 'visibility'>,
) {
  const did = getAgentDid(agent)
  if (!did) throw new Error('Missing DID for private post')
  const rooms = await ensurePrivateRooms(agent, did)
  const client = getClient()
  if (!client) throw new Error('Matrix client not initialized')
  const content: PrivatePostEvent = {
    id: nanoid(),
    authorDid: payload.authorDid,
    createdAt: nowIso(),
    text: payload.text,
    media: payload.media,
    visibility: 'private',
    replyToId: payload.replyToId,
  }
  await client.sendEvent(rooms.feedRoomId, PRIVATE_POST_EVENT, content)
  return content
}

export async function postPrivateStory(
  agent: BskyAgent,
  payload: Omit<PrivateStoryEvent, 'id' | 'createdAt'>,
) {
  const did = getAgentDid(agent)
  if (!did) throw new Error('Missing DID for private story')
  const rooms = await ensurePrivateRooms(agent, did)
  const client = getClient()
  if (!client) throw new Error('Matrix client not initialized')
  const content: PrivateStoryEvent = {
    id: nanoid(),
    authorDid: payload.authorDid,
    createdAt: nowIso(),
    expiresAt: payload.expiresAt,
    media: payload.media,
    textOverlays: payload.textOverlays,
  }
  await client.sendEvent(rooms.feedRoomId, PRIVATE_STORY_EVENT, content)
  return content
}

export async function listPrivateFeed(targetDid: string, agent: BskyAgent) {
  const endpoints = await getPrivateEndpoints(targetDid, agent)
  if (!endpoints?.feedRoomId) return []
  await ensureSilentMatrixIdentity({agent})
  const client = getClient()
  if (!client) return []
  try {
    await client.joinRoom(endpoints.feedRoomId)
  } catch {}
  await hideRoomsInAccountData([endpoints.feedRoomId], 'privateFeed', targetDid)
  const events = await listRoomEvents(endpoints.feedRoomId, 500)
  const posts: PrivatePostEvent[] = []
  for (const raw of events) {
    const {type, content} = parseMatrixEvent(raw)
    if (type === PRIVATE_POST_EVENT) {
      const parsed = content as PrivatePostEvent
      if (parsed?.id) posts.push(parsed)
    }
  }
  return posts.sort((a, b) => {
    const at = Date.parse(a.createdAt)
    const bt = Date.parse(b.createdAt)
    return bt - at
  })
}

export async function listPrivateStories(targetDid: string, agent: BskyAgent) {
  const endpoints = await getPrivateEndpoints(targetDid, agent)
  if (!endpoints?.feedRoomId) return []
  await ensureSilentMatrixIdentity({agent})
  const client = getClient()
  if (!client) return []
  try {
    await client.joinRoom(endpoints.feedRoomId)
  } catch {}
  await hideRoomsInAccountData([endpoints.feedRoomId], 'privateFeed', targetDid)
  const events = await listRoomEvents(endpoints.feedRoomId, 200)
  const now = Date.now()
  const stories: PrivateStoryEvent[] = []
  for (const raw of events) {
    const {type, content} = parseMatrixEvent(raw)
    if (type === PRIVATE_STORY_EVENT) {
      const parsed = content as PrivateStoryEvent
      const expiresAt = Date.parse(parsed?.expiresAt || '')
      if (parsed?.id && (!expiresAt || expiresAt > now)) {
        stories.push(parsed)
      }
    }
  }
  usePrivateStore.getState().setStoryRing(targetDid, stories.length)
  return stories.sort((a, b) => {
    const at = Date.parse(a.createdAt)
    const bt = Date.parse(b.createdAt)
    return bt - at
  })
}

export async function sendPrivateReaction(
  agent: BskyAgent,
  targetId: string,
) {
  const did = getAgentDid(agent)
  if (!did) throw new Error('Missing DID for private reaction')
  const rooms = await ensurePrivateRooms(agent, did)
  const client = getClient()
  if (!client) throw new Error('Matrix client not initialized')
  await client.sendEvent(rooms.feedRoomId, PRIVATE_REACTION_EVENT, {
    targetId,
    byDid: did,
    reaction: 'like',
    createdAt: nowIso(),
  })
}

export async function sendPrivateComment(
  agent: BskyAgent,
  targetId: string,
  text: string,
) {
  const did = getAgentDid(agent)
  if (!did) throw new Error('Missing DID for private comment')
  const rooms = await ensurePrivateRooms(agent, did)
  const client = getClient()
  if (!client) throw new Error('Matrix client not initialized')
  await client.sendEvent(rooms.feedRoomId, PRIVATE_COMMENT_EVENT, {
    targetId,
    byDid: did,
    text,
    createdAt: nowIso(),
  })
}

export async function removePrivateAccess(
  agent: BskyAgent,
  targetDid: string,
) {
  const ownerDid = getAgentDid(agent)
  if (!ownerDid) return
  const privacy = await getPrivacySettings(ownerDid, agent)
  if (!privacy?.isPrivate) return
  const rooms = await ensurePrivateRooms(agent, ownerDid)
  const profile = await getMatrixProfile(targetDid, agent)
  const mxid = profile?.mxid
  if (!mxid) return
  const client = getClient()
  if (!client) return
  try {
    await client.kick(rooms.audienceRoomId, mxid, 'Private account blocked')
  } catch {}
  try {
    await client.kick(rooms.feedRoomId, mxid, 'Private account blocked')
  } catch {}
}
